import { useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Button,
  Flex,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { CustomInput } from '../../CustomInput';
import { IconFiles } from '@tabler/icons-react';
import { fetchImagesFromFolderWithToken } from '@commons/storageApi';
import { ImageFile } from '../fields/types';
import { useAppSelector } from '@store';
import { DigiStorageAccount } from '@/users/pages/UserSettings/DigiStorageAccount';

interface PhotoFetchModalProps {
  onPhotosAdded: (images: ImageFile[], folderPath: string) => void;
}

export const PhotoFetchModal = ({ onPhotosAdded }: PhotoFetchModalProps) => {
  const intl = useIntl();
  const { settings } = useAppSelector((state) => state.settings);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [photoFolderPath, setPhotoFolderPath] = useState('');
  const [error, setError] = useState('');

  const handleFetchPhotos = async () => {
    if (!settings?.digiStorageToken) {
      setError(
        intl.formatMessage({ id: 'digiStorageTokenRequired' }) ||
          'DigiStorage token is required. Please generate a token in Settings.'
      );
      return;
    }

    if (!photoFolderPath) {
      setError(
        intl.formatMessage({ id: 'folderPathRequired' }) ||
          'Folder path is required'
      );
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const images = await fetchImagesFromFolderWithToken(
        settings.digiStorageToken,
        photoFolderPath,
        {
          failedToAuthenticate: intl.formatMessage({
            id: 'digiStorageFailedToAuthenticate',
          }),
          invalidResponseTokenNotFound: intl.formatMessage({
            id: 'digiStorageInvalidResponseTokenNotFound',
          }),
          networkError: intl.formatMessage({ id: 'digiStorageNetworkError' }),
          failedToGetMountId: intl.formatMessage({
            id: 'digiStorageFailedToGetMountId',
          }),
          noMountsFound: intl.formatMessage({ id: 'digiStorageNoMountsFound' }),
          failedToListFolder: intl.formatMessage({
            id: 'digiStorageFailedToListFolder',
          }),
          folderNotFound: intl.formatMessage({
            id: 'digiStorageFolderNotFound',
          }),
          failedToGetDownloadLink: intl.formatMessage({
            id: 'digiStorageFailedToGetDownloadLink',
          }),
          invalidResponseDownloadLinkNotFound: intl.formatMessage({
            id: 'digiStorageInvalidResponseDownloadLinkNotFound',
          }),
          failedToFetchImages: intl.formatMessage({
            id: 'digiStorageFailedToFetchImages',
          }),
        }
      );

      onPhotosAdded(images, photoFolderPath);

      // Reset and close modal
      setIsModalOpen(false);
      setPhotoFolderPath('');
    } catch (err: any) {
      setError(
        err.message ||
          intl.formatMessage({ id: 'digiStorageFailedToFetchImages' })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setError('');
    setPhotoFolderPath('');
  };

  return (
    <>
      <Flex justify="flex-end" w="100%">
        <Button
          id="photo-fetch-modal-trigger-button"
          variant="default"
          size="sm"
          onClick={() => setIsModalOpen(true)}
          leftSection={<IconFiles size={16} />}
        >
          {intl.formatMessage({ id: 'fetchPhotosFromStorage' })}
        </Button>
      </Flex>

      <Modal
        centered
        opened={isModalOpen}
        onClose={handleCancel}
        title={intl.formatMessage({ id: 'fetchPhotosFromStorage' })}
        zIndex={1000}
      >
        <Stack gap="md">
          <Paper p="md" withBorder>
            <Group gap="md" align="center" justify="space-between">
              <DigiStorageAccount
                email={settings?.digiStorageEmail}
                hasToken={!!settings?.digiStorageToken}
                onGenerateToken={() => {
                  // Close modal and let user generate token in settings
                  setIsModalOpen(false);
                  notifications.show({
                    color: 'blue',
                    title: intl.formatMessage({ id: 'information' }),
                    message:
                      intl.formatMessage({
                        id: 'pleaseGenerateTokenInSettings',
                      }) ||
                      'Please generate a DigiStorage token in Settings first.',
                  });
                }}
              />
            </Group>
          </Paper>

          <CustomInput
            type="text"
            value={photoFolderPath}
            name="photoFolderPath"
            label={intl.formatMessage({ id: 'folderPath' })}
            onChange={(value) => setPhotoFolderPath(value)}
          />
          {error && (
            <Text size="sm" c="red">
              {error}
            </Text>
          )}

          <Group justify="flex-end" mt="md">
            <Button
              id="photo-fetch-modal-cancel-button"
              variant="default"
              onClick={handleCancel}
              disabled={isLoading}
            >
              {intl.formatMessage({ id: 'cancel' })}
            </Button>
            <Button
              id="photo-fetch-modal-fetch-button"
              onClick={handleFetchPhotos}
              disabled={isLoading || !settings?.digiStorageToken}
              leftSection={isLoading ? <Loader size="xs" /> : undefined}
            >
              {isLoading
                ? intl.formatMessage({ id: 'fetching' })
                : intl.formatMessage({ id: 'fetchPhotos' })}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
